import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Wind, Trash2, Send, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../lib/i18n";
import { track } from "../lib/ga";

function BreathingCircle() {
  const { t } = useLang();
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(t("breathe.ready"));

  useEffect(() => {
    if (!running) { setPhase(t("breathe.ready")); return; }
    let mounted = true;
    const phases = [t("breathe.phase.in"), t("breathe.phase.hold"), t("breathe.phase.out"), t("breathe.phase.pause")];
    let i = 0;
    setPhase(phases[i]);
    const iv = setInterval(() => {
      if (!mounted) return;
      i = (i + 1) % phases.length;
      setPhase(phases[i]);
    }, 2000);
    return () => { mounted = false; clearInterval(iv); };
     
  }, [running]);

  return (
    <div className="bg-white border border-[#E8E6E1] rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center" data-testid="breathing-card">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#81B29A]/15 text-[#73A38B] text-xs font-semibold uppercase tracking-wider">
        <Wind className="w-3.5 h-3.5" /> {t("breathe.badge")}
      </div>
      <h2 className="font-heading text-3xl md:text-4xl text-[#3D405B] mt-3">{t("breathe.h2")}</h2>
      <p className="text-[#8A8D91] mt-2">{t("breathe.instructions")}</p>

      <div className="relative h-64 md:h-80 grid place-items-center my-8">
        <div className={`absolute w-48 h-48 md:w-56 md:h-56 rounded-full bg-[#81B29A]/30 ${running ? "animate-breathe" : ""}`} />
        <div className={`absolute w-32 h-32 md:w-40 md:h-40 rounded-full bg-[#81B29A]/60 ${running ? "animate-breathe" : ""}`} style={{ animationDelay: "0.3s" }} />
        <div className={`relative w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#81B29A] grid place-items-center text-white font-heading font-medium shadow-md text-sm text-center px-2 ${running ? "animate-breathe" : ""}`}>
          {phase}
        </div>
      </div>

      <button
        onClick={() => setRunning((r) => !r)}
        data-testid="breathing-toggle"
        className={`rounded-full px-6 py-3 font-medium transition-colors shadow-sm ${
          running ? "bg-[#3D405B] text-white" : "bg-[#81B29A] text-white hover:bg-[#73A38B]"
        }`}
      >
        {running ? t("breathe.stop") : t("breathe.start")}
      </button>
    </div>
  );
}

function WorryBox() {
  const { t } = useLang();
  const [content, setContent] = useState("");
  const [worries, setWorries] = useState([]);
  const [throwing, setThrowing] = useState(null);

  const load = () => api.get("/worries").then((r) => setWorries(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    const { data } = await api.post("/worries", { content });
    track.worryDropped();
    setContent("");
    setWorries((w) => [data, ...w]);
    toast.success(t("worry.saved"));
  };

  const release = async (id) => {
    setThrowing(id);
    setTimeout(async () => {
      await api.delete(`/worries/${id}`);
      setWorries((w) => w.filter((it) => it.id !== id));
      setThrowing(null);
    }, 1800);
  };

  return (
    <div className="bg-white border border-[#E8E6E1] rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="worry-box">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E07A5F]/15 text-[#D56D52] text-xs font-semibold uppercase tracking-wider">
        {t("worry.badge")}
      </div>
      <h2 className="font-heading text-3xl md:text-4xl text-[#3D405B] mt-3">{t("worry.title")}</h2>
      <p className="text-[#8A8D91] mt-2">{t("worry.subtitle")}</p>

      <form onSubmit={submit} className="mt-5 flex gap-2 flex-col sm:flex-row" data-testid="worry-form">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={2}
          placeholder={t("worry.placeholder")}
          data-testid="worry-input"
          className="flex-1 bg-[#FDFBF7] border border-[#E8E6E1] rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
        />
        <button
          type="submit"
          data-testid="worry-submit"
          disabled={!content.trim()}
          className="bg-[#E07A5F] text-white rounded-full px-5 py-3 font-medium hover:bg-[#D56D52] transition-colors disabled:opacity-50 inline-flex items-center gap-2 self-start"
        >
          <Send className="w-4 h-4" /> {t("worry.submit")}
        </button>
      </form>

      <div className="mt-6 space-y-3" data-testid="worry-list">
        {worries.length === 0 && (
          <div className="text-sm text-[#8A8D91] italic">{t("worry.empty")}</div>
        )}
        {worries.map((w) => (
          <div
            key={w.id}
            data-testid={`worry-item-${w.id}`}
            className={`relative bg-[#F4F1DE] border border-[#E8E6E1] rounded-2xl p-4 flex items-start justify-between gap-3 ${
              throwing === w.id ? "animate-float-up pointer-events-none" : ""
            }`}
          >
            <p className="text-[#3D405B] text-sm leading-relaxed flex-1">{w.content}</p>
            <button
              onClick={() => release(w.id)}
              data-testid={`worry-release-${w.id}`}
              title={t("worry.release")}
              className="text-[#8A8D91] hover:text-[#E07A5F] transition-colors p-2 rounded-full hover:bg-white"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MantraGenerator() {
  const { t, lang } = useLang();
  const [mantra, setMantra] = useState("");
  const load = () => api.get("/mantras/random").then((r) => setMantra(r.data.mantra));
  useEffect(() => { load(); }, [lang]);
  return (
    <div className="bg-[#3D405B] rounded-3xl p-8 md:p-10 text-white shadow-[0_8px_30px_rgb(0,0,0,0.08)]" data-testid="mantra-card">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#F2CC8F] text-xs font-semibold uppercase tracking-wider">
        {t("mantra.badge")}
      </div>
      <p className="font-heading text-2xl md:text-3xl mt-4 leading-snug">« {mantra} »</p>
      <button
        onClick={load}
        data-testid="mantra-refresh"
        className="mt-5 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 text-sm transition-colors"
      >
        <RefreshCcw className="w-4 h-4" /> {t("mantra.refresh")}
      </button>
    </div>
  );
}

export default function Defouloir() {
  const { t } = useLang();
  return (
    <div className="space-y-8" data-testid="defouloir-page">
      <header>
        <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B]">{t("breathe.title")}</h1>
        <p className="text-[#8A8D91] mt-2 max-w-2xl">{t("breathe.subtitle")}</p>
      </header>
      <BreathingCircle />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WorryBox />
        <MantraGenerator />
      </div>
    </div>
  );
}
